import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Coins, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AVATAR_REGISTRY } from "../lib/avatars";
import LoadingState from "../components/LoadingState";
import AvatarUnlockCelebration from "../components/AvatarUnlockCelebration";
import { useTranslation } from "react-i18next";

const THEME_IMG = {
  zen_garden: "https://images.unsplash.com/photo-1762932922297-767ca87bfe3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHx6ZW4lMjBnYXJkZW4lMjBtaW5pbWFsfGVufDB8fHx8MTc3NjU5MzUwMXww&ixlib=rb-4.1.0&q=85",
  nature_trail: "https://images.pexels.com/photos/32594449/pexels-photo-32594449.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  yoga_studio: "https://images.unsplash.com/photo-1761971975973-cbb3e59263de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIweW9nYSUyMHN0dWRpb3xlbnwwfHx8fDE3NzY1OTM1MDF8MA&ixlib=rb-4.1.0&q=85",
};

const AVATAR_IMG = Object.fromEntries(
  Object.entries(AVATAR_REGISTRY).map(([k, v]) => [k, v.img])
);

export default function Themes() {
  const { t } = useTranslation();
  const { setProfile, profile } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [celebrate, setCelebrate] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/themes");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t("themes.couldnt_load"));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const pickTheme = async (id) => {
    try {
      const { data } = await api.post("/themes/select", { theme_id: id });
      setProfile(data);
      toast.success(t("themes.toast_theme_selected"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("themes.toast_cannot_select"));
    }
  };
  const pickAvatar = async (a, wasLocked = false) => {
    try {
      const { data } = await api.post("/avatar/select", { avatar_id: a.avatar_id });
      setProfile(data);
      if (wasLocked) {
        setCelebrate({
          name: t(`themes.avatar_${a.avatar_id}`, a.name),
          bonus: AVATAR_REGISTRY[a.avatar_id]?.bonus?.label
            ? t(`themes.avatar_${a.avatar_id}_bonus`, AVATAR_REGISTRY[a.avatar_id].bonus.label)
            : null,
          img: AVATAR_IMG[a.avatar_id],
        });
      } else {
        toast.success(t("themes.toast_avatar_selected"));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("themes.toast_cannot_select"));
    }
  };

  if (!data || error) return <LoadingState error={error} onRetry={load} rows={4} testid="themes" />;

  return (
    <div className="space-y-12">
      <section>
        <p className="text-sm uppercase tracking-[0.25em] text-[#8AA48F] mb-2">{t("themes.atmosphere")}</p>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-4xl font-light tracking-tight mb-6">
          {t("themes.choose_world")}
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {data.themes.map((th) => (
            <div
              key={th.theme_id}
              data-testid={`theme-${th.theme_id}`}
              className={`rounded-3xl overflow-hidden border ${
                th.selected ? "border-[#5F7161] ring-2 ring-[#5F7161]/30" : "border-[#E6E2D8]"
              } bg-white relative`}
            >
              <div className="relative h-44 overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-all"
                  style={{
                    backgroundImage: `url(${THEME_IMG[th.theme_id]})`,
                    filter: th.unlocked ? "none" : "blur(3px) brightness(0.55) saturate(0.4)",
                  }}
                />
                {!th.unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center bg-white/85 backdrop-blur-sm shadow-lg"
                      aria-label={t("themes.locked_theme")}
                    >
                      <Lock size={22} strokeWidth={2} className="text-[#2C332A]" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-medium">{th.name}</h3>
                  {th.selected && <Check className="text-[#5F7161]" size={18} strokeWidth={2} />}
                </div>
                {th.unlocked ? (
                  <Button
                    data-testid={`select-theme-${th.theme_id}`}
                    disabled={th.selected}
                    onClick={() => pickTheme(th.theme_id)}
                    className="w-full rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white disabled:bg-[#E6E2D8] disabled:text-[#6E756B] disabled:opacity-100"
                  >
                    {th.selected ? t("themes.active") : t("themes.select")}
                  </Button>
                ) : (
                  <Button
                    data-testid={`unlock-theme-${th.theme_id}`}
                    variant="outline"
                    onClick={() => pickTheme(th.theme_id)}
                    disabled={profile.coins < th.unlock_cost}
                    className="w-full rounded-full border-[#E6E2D8]"
                  >
                    <Lock size={13} className="mr-1.5" />
                    <Coins size={14} className="mr-1" /> {t("themes.unlock_cost", { coins: th.unlock_cost })}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="text-sm uppercase tracking-[0.25em] text-[#8AA48F] mb-2">{t("themes.avatar_label")}</p>
        <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl font-light tracking-tight mb-6">
          {t("themes.choose_athlete")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {data.avatars.map((a) => (
            <div
              key={a.avatar_id}
              data-testid={`avatar-${a.avatar_id}`}
              className={`p-6 rounded-3xl border ${a.selected ? "border-[#5F7161] ring-2 ring-[#5F7161]/30" : "border-[#E6E2D8]"} bg-white text-center`}
            >
              <div className="relative w-28 h-28 mx-auto mb-3">
                <div
                  className="w-full h-full rounded-full bg-cover bg-center border-2 border-white shadow-[0_8px_32px_rgba(44,51,42,0.08)]"
                  style={{
                    backgroundImage: `url(${AVATAR_IMG[a.avatar_id]})`,
                    filter: a.unlocked ? "none" : "blur(2px) brightness(0.6) saturate(0.4)",
                  }}
                />
                {!a.unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center bg-white/85 backdrop-blur-sm shadow">
                      <Lock size={14} strokeWidth={2} className="text-[#2C332A]" />
                    </div>
                  </div>
                )}
              </div>
              <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-lg font-medium mb-1">{t(`themes.avatar_${a.avatar_id}`, a.name)}</p>
              {AVATAR_REGISTRY[a.avatar_id]?.bonus?.label && (
                <p className="text-xs text-[#5F7161] mb-3 flex items-center justify-center gap-1">
                  <Sparkles size={11} strokeWidth={1.8} />
                  {t(`themes.avatar_${a.avatar_id}_bonus`, AVATAR_REGISTRY[a.avatar_id].bonus.label)}
                </p>
              )}
              {a.unlocked ? (
                <Button
                  data-testid={`select-avatar-${a.avatar_id}`}
                  disabled={a.selected}
                  onClick={() => pickAvatar(a, false)}
                  className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white disabled:bg-[#E6E2D8] disabled:text-[#6E756B] disabled:opacity-100"
                >
                  {a.selected ? t("themes.active") : t("themes.select")}
                </Button>
              ) : (
                <Button
                  data-testid={`unlock-avatar-${a.avatar_id}`}
                  variant="outline"
                  onClick={() => pickAvatar(a, true)}
                  disabled={profile.coins < a.unlock_cost}
                  className="rounded-full border-[#E6E2D8]"
                >
                  <Lock size={12} className="mr-1.5" />
                  <Coins size={14} className="mr-1" /> {a.unlock_cost}
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>

      <AvatarUnlockCelebration open={!!celebrate} avatar={celebrate} onDone={() => setCelebrate(null)} />
    </div>
  );
}
